import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { calculateHeartLayout, HeartPhraseSource, PlacedPhrase, RenderOptions } from './HeartRenderer';

interface HeartCanvasProps {
  phrases: HeartPhraseSource[];
  settings?: {
    love_ratio?: number;
    nickname_ratio?: number;
    min_font_size?: number;
    max_font_size?: number;
    text_density?: 'compact' | 'balanced' | 'spacious';
    animation_style?: 'none' | 'subtle' | 'gentle';
    color_palette?: string[];
    highlight_frequency?: number;
    allow_viewer_rearrange?: boolean;
  };
  seed?: number;
  onLayoutReady?: (total: number, loveRatio: number, nickRatio: number) => void;
  onPhraseHover?: (hovered: boolean) => void;
}

export const HeartCanvas: React.FC<HeartCanvasProps> = ({
  phrases,
  settings,
  seed = 42,
  onLayoutReady,
  onPhraseHover,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [placedItems, setPlacedItems] = useState<PlacedPhrase[]>([]);
  const [isRendered, setIsRendered] = useState(false);
  const [hoveredPhrase, setHoveredPhrase] = useState<PlacedPhrase | null>(null);

  // Measure text width using an offscreen canvas context
  const measureCanvas = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    return canvas.getContext('2d');
  }, []);

  const measureTextWidth = useCallback(
    (text: string, fontSize: number): number => {
      if (!measureCanvas) {
        // Fallback estimate
        return text.length * fontSize * 0.6;
      }
      measureCanvas.font = `400 ${fontSize}px 'Inter', 'Noto Sans Bengali', 'Noto Sans Devanagari', 'Noto Sans JP', sans-serif`;
      return measureCanvas.measureText(text).width;
    },
    [measureCanvas]
  );

  // Observe container dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDimensions({ width: Math.round(width), height: Math.round(height) });
        }
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const onLayoutReadyRef = useRef(onLayoutReady);
  useEffect(() => {
    onLayoutReadyRef.current = onLayoutReady;
  }, [onLayoutReady]);

  // Recalculate layout on dimension, phrase, or setting change
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0 || phrases.length === 0) {
      return;
    }

    const options: RenderOptions = {
      width: dimensions.width,
      height: dimensions.height,
      loveRatio: settings?.love_ratio ?? 70,
      nicknameRatio: settings?.nickname_ratio ?? 30,
      minFontSize: settings?.min_font_size ?? 11,
      maxFontSize: settings?.max_font_size ?? 22,
      textDensity: settings?.text_density ?? 'balanced',
      animationStyle: settings?.animation_style ?? 'subtle',
      colorPalette: settings?.color_palette,
      highlightFrequency: settings?.highlight_frequency ?? 0.15,
      seed,
    };

    let cancelled = false;

    // Ensure fonts are loaded before final layout computation
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (cancelled) return;
        const layout = calculateHeartLayout(phrases, options, measureTextWidth);
        setPlacedItems(layout.phrases);
        setIsRendered(true);
        onLayoutReadyRef.current?.(layout.totalPhrases, layout.actualLoveRatio, layout.actualNicknameRatio);
      });
    } else {
      const layout = calculateHeartLayout(phrases, options, measureTextWidth);
      setPlacedItems(layout.phrases);
      setIsRendered(true);
      onLayoutReadyRef.current?.(layout.totalPhrases, layout.actualLoveRatio, layout.actualNicknameRatio);
    }

    return () => {
      cancelled = true;
    };
  }, [dimensions.width, dimensions.height, phrases, settings, seed, measureTextWidth]);

  const animStyle = settings?.animation_style ?? 'subtle';
  const animationClass =
    animStyle === 'none'
      ? 'animate-none'
      : animStyle === 'gentle'
        ? 'animate-gentle'
        : 'animate-subtle';

  return (
    <div
      ref={containerRef}
      className={`heart-container ${isRendered ? 'visible' : 'measuring'} ${animationClass}`}
      role="region"
      aria-label="Typographic Heart"
    >
      <div
        key={`stage-${seed}`}
        className="heart-stage"
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        {placedItems.map(item => {
          const isRTL = item.language === 'Arabic';
          return (
            <span
              key={`${seed}-${item.id}`}
              className={`heart-phrase ${item.isHighlight ? 'phrase-highlight' : ''} ${item.type === 'nickname' ? 'phrase-nickname' : 'phrase-love'}`}
              style={{
                position: 'absolute',
                left: `${item.x}px`,
                top: `${item.y}px`,
                fontSize: `${item.fontSize}px`,
                color: item.color,
                direction: isRTL ? 'rtl' : 'ltr',
                animationDelay: `${item.phaseDelay}ms`,
              }}
              onMouseEnter={() => {
                setHoveredPhrase(item);
                onPhraseHover?.(true);
              }}
              onMouseLeave={() => {
                setHoveredPhrase(null);
                onPhraseHover?.(false);
              }}
              onFocus={() => {
                setHoveredPhrase(item);
                onPhraseHover?.(true);
              }}
              onBlur={() => {
                setHoveredPhrase(null);
                onPhraseHover?.(false);
              }}
              tabIndex={0}
              aria-label={`${item.text}${item.language ? ` (${item.language})` : ''}`}
            >
              {item.text}
            </span>
          );
        })}
      </div>

      {hoveredPhrase && (
        <div className="heart-status-bar" aria-live="polite">
          <span className="status-text">{hoveredPhrase.text}</span>
          {hoveredPhrase.language && (
            <span className="status-meta">{hoveredPhrase.language}</span>
          )}
          {hoveredPhrase.type === 'nickname' && (
            <span className="status-meta">nickname</span>
          )}
        </div>
      )}
    </div>
  );
};
